import { useEffect, useRef, useState } from 'react';
import { acquireDeliveryAction, releaseDeliveryAction, getDeliveryWorkspace, getDeliveryAction,
  updateDeliveryWorkspace, hasDeliveryWorkspaceWork, markPreparedDeliveryWorkspace } from '@/utils/deliveryDraftStore';
import { loadPhotoDraft, readPhotoRecovery, savePhotoDraft, discardPhotoDraft, restorePhotoFields, restorePhotoRecovery, photoDraftHasWork } from '@/utils/deliveryPhotoDraftPersistence';
import { prepareDeliveryPhoto } from '@/utils/prepareDeliveryPhoto';
import { withDeliveryCopyOperation } from '@/utils/deliveryCopyOperation';

// TCA-26102418 rollout switch. Disable preparation for rollback without
// removing the saved-copy or receipt recovery contracts.
export const DELIVERY_PHOTO_PREPARATION_ENABLED = true;

export function useDeliveryPhotoPreparation({ enabled, opening, work, owns, canAct, onRestored, discoverOther = false, paused = false, safelyRetained = false }) {
  const context = useRef(null);
  const [, render] = useState(0);
  const latest = useRef({ owns, canAct, opening, enabled, workspaceId: work?.id });
  latest.current = { owns, canAct, opening, enabled, paused, workspaceId: work?.id };
  const currentWork = () => getDeliveryWorkspace(opening.principal, opening.orderId, opening.kind);
  const current = (ctx) => context.current === ctx && ctx.live && latest.current.enabled
    && latest.current.opening === ctx.opening && latest.current.workspaceId === ctx.workspaceId && latest.current.owns();
  const notify = (ctx) => { if (current(ctx)) render((value) => value + 1); };

  // One serialized writer per opening; IndexedDB compare-and-swap arbitrates
  // different tabs/openings. Never replay a rejected write over a new revision.
  const persist = (ctx, fields, recovery, lease = ctx.lease, operationContext) => {
    const run = async () => {
      if (!current(ctx) || ctx.completed || latest.current.paused) throw new DOMException('Opening retired or recovery review active', 'AbortError');
      if (ctx.writeError) throw ctx.writeError;
      if (ctx.savedFields === fields && recovery === undefined) return;
      ctx.saving = true; notify(ctx);
      try {
        const isCurrent = () => current(ctx) && !ctx.completed && !latest.current.paused;
        const saved = await withDeliveryCopyOperation(opening.orderId, () => {
          if (!isCurrent()) throw new DOMException('Opening retired', 'AbortError');
          return savePhotoDraft(opening.principal, opening.orderId, opening.kind, fields, ctx.revision, recovery, { isCurrent });
        }, { context: operationContext, requireCrossContext: true });
        ctx.revision = saved.revision;
        ctx.savedFields = fields;
        const value = currentWork();
        if (current(ctx) && value?.id === ctx.workspaceId) updateDeliveryWorkspace(value, { photoDraftRevision: saved.revision }, lease);
      } catch (error) { ctx.writeError = error; throw error; }
      finally { ctx.saving = false; notify(ctx); }
    };
    const promise = ctx.writer.then(run);
    ctx.writer = promise.catch(() => {});
    return promise;
  };

  useEffect(() => {
    if (!enabled || !work || !owns()) return undefined;
    markPreparedDeliveryWorkspace(work);
    const ctx = { live: true, opening, workspaceId: work.id, ready: false, revision: null, savedFields: null,
      writer: Promise.resolve(), saving: false, preparing: false, writeError: null, loadError: null, cancelled: false };
    context.current = ctx;
    const otherKind = opening.kind === 'confirmation' ? 'amendment' : 'confirmation';
    Promise.all([readPhotoRecovery(opening.principal, opening.orderId, opening.kind),
      discoverOther ? readPhotoRecovery(opening.principal, opening.orderId, otherKind) : null]).then(([recoveryState, otherState]) => {
      if (!current(ctx)) return;
      const saved = recoveryState.active;
      const other = otherState?.active;
      ctx.otherDraft = photoDraftHasWork(other, otherKind) ? { kind: otherKind, revision: other.revision } : null;
      const attempt = saved?.recovery?.attempt;
      const resolution = recoveryState.resolution;
      // A single active copy is not a conflict. Alternatives and unresolved
      // identities still require the existing copy-selection coordinator.
      const selectedActive = !!saved?.copyId && resolution?.selections?.[opening.kind] === saved.copyId;
      ctx.conflict = !!((recoveryState.alternatives.length && !selectedActive) || otherState?.alternatives.length
        || resolution?.selections?.[opening.kind] === null || resolution?.legacyFailureHeld
        || resolution?.evidence?.some((item) => !item.accepted && !item.abandoned
          && (item.kind !== attempt?.kind || item.captureId !== attempt?.captureId)));
      let value = currentWork();
      if (!value || value.id !== ctx.workspaceId || getDeliveryAction(value)) return;
      ctx.revision = saved?.revision ?? null;
      if (saved && value.photoDraftRevision !== saved.revision) {
        if (hasDeliveryWorkspaceWork(value)) throw new Error('Saved and running-tab drafts differ. Both are retained; close this opening and review recovery before continuing.');
        const recovery = restorePhotoRecovery(saved.recovery);
        const fields = restorePhotoFields(saved.fields);
        value = updateDeliveryWorkspace(value, { fields, ...recovery, photoDraftRevision: saved.revision });
        onRestored?.(value.id);
        ctx.savedFields = value.fields;
        ctx.restored = true;
        ctx.needsReview = opening.kind === 'amendment';
      } else if (saved) {
        // Same-tab reopen must also retire a previously pending transport. A
        // retained lease above prevents doing this while it is still running.
        if (value.attempt?.status === 'pending') {
          value = updateDeliveryWorkspace(value, { attempt: { ...value.attempt, status: 'unknown' } });
        }
      }
      ctx.returningAttemptId = value.attempt?.captureId || null;
      ctx.ready = true; notify(ctx);
    }).catch((error) => { ctx.loadError = error; notify(ctx); });
    return () => { ctx.live = false; ctx.controller?.abort(); };
    // Initialization belongs to a workspace/opening, not changing fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, opening, work?.id, discoverOther]);

  const prepare = async () => {
    const ctx = context.current;
    if (latest.current.paused || !ctx || !current(ctx) || ctx.completed || !ctx.ready || ctx.preparing || ctx.needsReview || ctx.conflict || ctx.writeError || currentWork()?.attempt || !latest.current.canAct()) return;
    let value = currentWork();
    const lease = acquireDeliveryAction(value, 'prepare-photos');
    if (!lease) return;
    ctx.lease = lease; ctx.preparing = true; ctx.cancelled = false;
    ctx.controller = new AbortController(); notify(ctx);
    try {
      await persist(ctx, value.fields); // Commit originals before decoding.
      for (let index = 0; index < value.fields.photos.length; index += 1) {
        if (!current(ctx) || ctx.controller.signal.aborted) break;
        const photo = value.fields.photos[index];
        if (photo.prepared || photo.preparationError) continue;
        ctx.progress = `Preparing photo ${index + 1} of ${value.fields.photos.length}`; notify(ctx);
        let prepared; let preparationError;
        try { prepared = await prepareDeliveryPhoto(photo.file, { signal: ctx.controller.signal }); }
        catch (error) {
          if (error.name === 'AbortError') break;
          preparationError = error.message;
        }
        if (!current(ctx) || ctx.controller.signal.aborted) break;
        const preview = prepared ? URL.createObjectURL(prepared.file) : photo.preview;
        const photos = value.fields.photos.map((item, i) => i === index ? { ...item, prepared, preparationError, preview } : item);
        const updated = updateDeliveryWorkspace(currentWork(), { fields: { ...value.fields, photos } }, lease);
        if (!updated) { if (prepared) URL.revokeObjectURL(preview); break; }
        value = updated;
        await persist(ctx, value.fields);
      }
    } catch (error) {
      if (error.name !== 'AbortError') ctx.writeError = error;
    } finally {
      ctx.preparing = false; ctx.progress = ''; ctx.lease = null;
      releaseDeliveryAction(lease); notify(ctx);
    }
  };

  const ctx = context.current;
  const clearedFields = opening.kind === 'confirmation' && (work?.fields.locationCleared || work?.fields.deliveryTime === '');
  useEffect(() => {
    if (paused || !enabled || !ctx || !current(ctx) || ctx.completed || !ctx.ready || ctx.needsReview || ctx.conflict || ctx.writeError || ctx.preparing || !work || getDeliveryAction(work)) return;
    if (ctx.savedFields !== work.fields && (hasDeliveryWorkspaceWork(work) || ctx.savedFields || clearedFields)) persist(ctx, work.fields).catch(() => {});
    if (!ctx.cancelled && latest.current.canAct() && work.fields.photos.some((p) => !p.prepared && !p.preparationError)) void prepare();
    // The writer skips identical snapshots and preparation takes a synchronous
    // action lease, so re-rendering cannot admit duplicate work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, paused, work, ctx?.ready, ctx?.needsReview, ctx?.cancelled]);

  const active = enabled && ctx && current(ctx) ? ctx : null;
  const pendingSave = !!work && !!(hasDeliveryWorkspaceWork(work) || active?.revision || active?.savedFields || clearedFields)
    && active?.savedFields !== work.fields;
  const saved = !!active?.ready && !active?.writeError && !pendingSave;
  useEffect(() => {
    if (!enabled || saved || safelyRetained || !pendingSave) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [enabled, saved, safelyRetained, work, pendingSave]);
  return {
    enabled,
    saved,
    pendingSave,
    ready: !!active?.ready && !active?.needsReview && !active?.conflict,
    needsReview: !!active?.needsReview,
    returningAttemptId: active?.returningAttemptId || null,
    needsCopyReview: !!(active?.conflict || active?.loadError || active?.writeError?.code === 'PHOTO_DRAFT_CONFLICT'),
    canRetry: !!active && !active.conflict && !active.loadError && active.writeError?.code !== 'PHOTO_DRAFT_CONFLICT'
      && !active.preparing && !!(active.writeError || active.cancelled),
    retryLabel: active?.writeError ? 'Try saving again' : 'Continue preparing photos',
    otherDraft: active?.otherDraft || null,
    // Submission uses the same serialized CAS writer as preparation. Resolve
    // only after the transaction commits; caller rechecks its opening/lease.
    commitRecovery: async (value, lease, operationContext) => {
      if (!active || !active.ready || active.needsReview || currentWork() !== value || getDeliveryAction(value) !== lease) throw new Error('Draft ownership changed; nothing was sent.');
      await persist(active, value.fields, { attempt: value.attempt, checkpoint: value.checkpoint }, lease, operationContext);
      if (!current(active)) throw new DOMException('Opening retired', 'AbortError');
    },
    complete: async (value, lease, operationContext) => {
      if (!active || !current(active) || value.attempt?.status !== 'accepted' || value.attempt.kind === 'failure' || getDeliveryAction(value) !== lease) throw new Error('Acceptance must be verified before removing a draft.');
      await active.writer;
      const isCurrent = () => current(active) && currentWork() === value && getDeliveryAction(value) === lease;
      if (!isCurrent()) throw new Error('Draft ownership changed; saved work was retained.');
      await withDeliveryCopyOperation(opening.orderId, () => discardPhotoDraft(opening.principal, opening.orderId, opening.kind, active.revision,
        { isCurrent, ...(opening.kind === 'confirmation' ? { verified: true } : {}) }), { context: operationContext, requireCrossContext: true });
      active.completed = true;
      if (!isCurrent()) throw new DOMException('Opening retired', 'AbortError');
    },
    discardLocal: async (value, lease) => {
      const acceptedFailureOnly = value.attempt?.kind === 'failure' && value.attempt.status === 'accepted';
      if (!active || !current(active) || !active.ready || active.needsReview || active.writeError || (value.attempt && !acceptedFailureOnly)
          || value.savedMediaRemoval || getDeliveryAction(value) !== lease) throw new Error('This draft cannot be discarded yet.');
      await active.writer;
      const isCurrent = () => current(active) && currentWork() === value && getDeliveryAction(value) === lease;
      if (!isCurrent()) throw new Error('Draft changed. Review it again before discarding.');
      await withDeliveryCopyOperation(opening.orderId, () => discardPhotoDraft(opening.principal, opening.orderId, opening.kind, active.revision,
        acceptedFailureOnly ? { acceptedFailureOnly: true, isCurrent } : { unsentOnly: true, isCurrent }), { requireCrossContext: true });
      active.completed = true;
      if (!isCurrent()) throw new DOMException('Opening retired', 'AbortError');
    },
    busy: !!active?.preparing,
    message: active?.loadError ? 'Could not open saved work. Your copies have not been changed.'
      : active?.conflict ? 'There is more than one saved version. Choose which one to continue.'
      : active?.writeError ? `Not saved to this device: ${active.writeError.message} Keep this page open and try saving again.`
      : active?.progress || (active?.cancelled ? 'Preparation cancelled. Your photos are still here.'
        : active?.ready && !saved ? 'Saving… Keep this page open.'
        : active?.ready && hasDeliveryWorkspaceWork(work) ? (active.restored ? 'Your saved work is back. Saved on this device.' : 'Saved on this device.')
        : active?.ready ? '' : 'Checking for saved work…'),
    review: () => { if (active && !latest.current.paused) { active.needsReview = false; notify(active); } },
    cancel: () => { if (active) { active.cancelled = true; active.controller?.abort(); notify(active); } },
    retry: async () => {
      if (latest.current.paused || !active || active.preparing || active.retrying || active.loadError || active.conflict) return;
      active.retrying = true;
      try {
        if (active.writeError) {
          // A failed write may have committed without acknowledgement. Only
          // retry when fresh readback proves the known revision still owns it.
          const savedDraft = await loadPhotoDraft(opening.principal, opening.orderId, opening.kind);
          if (!current(active)) return;
          if ((savedDraft?.revision ?? null) !== active.revision) throw Object.assign(new Error('Saved draft changed. Both copies are retained; choose which to continue.'), { code: 'PHOTO_DRAFT_CONFLICT' });
          active.writeError = null;
          const value = currentWork();
          await persist(active, value.fields, { attempt: value.attempt, checkpoint: value.checkpoint });
        }
        active.cancelled = false; notify(active);
        await prepare();
      } catch (error) { active.writeError = error; }
      finally { active.retrying = false; notify(active); }
    },
  };
}
